import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import { useNavigationBar } from "../../../context/NavigationContext";
import { ContentState, EventLocationType, SKEventArtifact } from "../../../models/SKArtifact";
import * as Yup from "yup";
import { SKLocationType } from "../../../api/shared/uploadService";
import "react-datepicker/dist/react-datepicker.css";
import { AddPlaceField } from "./container/components/AddPlaceField";
// import { AddTextAreaField } from "./container/components/AddTextAreaField";
// import { AddTextField } from "./container/components/AddTextField";
import { capitalizeFirstLetter } from "../../../utils/stringFunctions";
import { DateTimePicker } from "./container/DateTimePicker";
import { Timestamp } from "firebase/firestore";
import { isEditable } from "@testing-library/user-event/dist/utils";
import { AddTextAreaField } from "../../../components/AddTextAreaField";
import { AddTextField } from "../../../components/AddTextField";
import { add } from "date-fns";

export default function AddEvent() {
  const { contentData, artifactId, updateContentData, loading, saveData, isEditable } = useContext(CreateArtifactContext);
  const { setNavProps } = useNavigationBar();
  const navigate = useNavigate();
  const [isValidForm, setIsValidForm] = useState(false);
  const [eventData, setEventData] = useState<SKEventArtifact | undefined>(contentData as SKEventArtifact);
  // const [startDate, setStartDate] = useState<Date | null>(Timestamp.now().toDate);
  // const [endDate, setEndDate] = useState<Date | null>(Timestamp.now().toDate);
  // const [startTime, setStartTime] = useState<Date | null>(Timestamp.now().toDate);
  // const [endTime, setEndTime] = useState<Date | null>(Timestamp.now().toDate);
  const [error, setError] = useState<string>("");
  // const [isInPerson, setIsInPerson] = useState(true);
  // const [selectedMediaType, setSelectedMediaType] = useState<SKLocationType>("In Person");

  const isFirebaseTimestamp = (value: any): value is Timestamp => {
    return value instanceof Timestamp && typeof value.toDate === "function" && typeof value.seconds === "number";
  };

  const schema = Yup.object().shape({
    eventName: Yup.string().required(),
    eventLocationType: Yup.string().required(),
    location: Yup.object({
      latitude: Yup.number().required("Latitude is required"),
      longitude: Yup.number().required("Longitude is required"),
      title: Yup.string().required("Title is required"),
      address: Yup.string().required("Address is required"),
      url: Yup.string().notRequired(),
    }).when("eventLocationType", {
      is: EventLocationType.inPerson,
      then: schema => schema.required("Event location is required"),
      otherwise: schema => schema.notRequired(),
    }),
    eventDescription: Yup.string().required(),
    startDate: Yup.mixed()
      .required("Start date is required")
      .test("is-timestamp", "Start date must be a valid Firebase Timestamp", value => isFirebaseTimestamp(value))
      .test("is-future-timestamp", "Start date must be a future date", value => {
        if (!isFirebaseTimestamp(value)) return false;
        return value.toDate() > new Date();
      }),
    endDate: Yup.mixed()
      .notRequired()
      .test("is-timestamp", "End date must be a valid Firebase Timestamp", value => !value || isFirebaseTimestamp(value))
      .test("is-greater-than-start", "End date must be greater than start date", function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate || !isFirebaseTimestamp(value) || !isFirebaseTimestamp(startDate)) return true;
        return value.toDate() > startDate.toDate();
      }),
  });

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(contentData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err) {
        let error = err as Yup.ValidationError;
        // setErrors(prev => ({ ...prev, [name]: error.message }));
        setIsValidForm(false);
        if (error.inner) {
          const errors: { path: string; message: string }[] = error.inner.map(error1 => ({
            path: error1.path || "unknown", // Field with the error
            message: error1.message, // Error message
          }));

          console.log("Validation errors:", errors);
        }
      }
    };
    validate();
  }, [contentData, schema]);

  useEffect(() => {
    setNavProps(prev => ({
      ...prev,
      backArrow: false,
      buttonTitle: "Done",
      title: "Add Event",
      getActionItems: undefined,
      enableLink: isEditable ? isValidForm : false,
      onNext: isEditable ? submit : undefined,
      onClose: () => navigate(-1),
      onEdit: undefined,
    }));
  }, [setNavProps, contentData, isEditable, isValidForm]);

  useEffect(() => {
    let data = contentData as SKEventArtifact;
    if (data && !data.startDate) {
      data.startDate = Timestamp.now();
    }
    if (data && !data.endDate) {
      data.endDate = Timestamp.now();
    }
    setEventData(data);
  }, [contentData]);

  useEffect(() => {
    let data = contentData as SKEventArtifact;
    if (data && !data.eventLocationType) {
      data.eventLocationType = EventLocationType.inPerson;
      updateContentData({
        eventLocationType: EventLocationType.inPerson,
      } as SKEventArtifact);
    }
  }, [contentData]);

  const submit = async () => {
    await saveData();
    window.history.back();
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      updateContentData({ startDate: Timestamp.fromDate(date) });
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      updateContentData({ endDate: Timestamp.fromDate(date) });
    }
  };

  return (
    <>
      <div className="flex flex-col mt-[12px] gap-[30px]">
        <div>
          <AddTextField
            name={"eventname"}
            title={"Add Event Name"}
            note={"Name of the actual event."}
            placeholder={"Event Name"}
            index={1}
            charAllowed={150}
            value={eventData?.eventName ?? ""}
            onChange={async e => {
              updateContentData({
                eventName: e.target.value,
              });
            }}
            disabled={!isEditable}
          />
        </div>
        <div className="relative w-full min-w-[200px]">
          <div className="flex items-center">
            <span className="content_count">2</span>
            <span className="content_title">Add Event Date and Time</span>
          </div>
          <DateTimePicker
            startNote={"Start date and time of the event."}
            endNote={"End date and end time of the event. Leave blank if not needed."}
            onStartDateChanged={handleStartDateChange}
            onEndDateChanged={handleEndDateChange}
            startTimestamp={eventData?.startDate}
            endTimestamp={eventData?.endDate}
            disabled={!isEditable}
          />
        </div>
        <div className="relative w-full min-w-[200px]">
          <div className="flex items-center">
            <span className="content_count">3</span>
            <span className="content_title">Add Event Location</span>
          </div>
          <div className="notes-class-content">Choose a place for an in-person event or online event details.</div>
          <label className="relative inline-flex items-center">
            <input
              type="radio"
              name="locationsType"
              value="In Person"
              checked={eventData?.eventLocationType === EventLocationType.inPerson}
              onChange={async e => {
                updateContentData({
                  eventLocationType: EventLocationType.inPerson,
                } as SKEventArtifact);
              }}
              className="absolute opacity-0 cursor-pointer"
              disabled={!isEditable}
            />
            <span
              className={`w-5 h-5 flex items-center justify-center border-2 rounded-full 
        ${eventData?.eventLocationType === EventLocationType.inPerson ? "bg-black border-black" : "border-black"}`}
            >
              {eventData?.eventLocationType === EventLocationType.inPerson && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12l5 5L20 7" />
                </svg>
              )}
            </span>
            <span className="ml-2">In Person</span>
          </label>

          <div className="my-2 thinseparator max-w-[480px]"></div>

          <label className="relative inline-flex items-center">
            <input
              type="radio"
              name="locationsType"
              value="Online"
              checked={eventData?.eventLocationType === EventLocationType.online}
              onChange={async e => {
                updateContentData({
                  eventLocationType: EventLocationType.online,
                } as SKEventArtifact);
              }}
              className="absolute opacity-0 cursor-pointer"
              disabled={!isEditable}
            />
            <span
              className={`w-5 h-5 flex items-center justify-center border-2 rounded-full 
        ${eventData?.eventLocationType === EventLocationType.online ? "bg-black border-black" : "border-black"}`}
            >
              {eventData?.eventLocationType === EventLocationType.online && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12l5 5L20 7" />
                </svg>
              )}
            </span>
            <span className="ml-2">Online</span>
          </label>

          <div className="my-2 thinseparator max-w-[480px]"></div>

          {eventData?.eventLocationType === EventLocationType.inPerson && (
            <div>
              <AddPlaceField
                name={"addplace"}
                title={""}
                note={"Add a place for an in-person event."}
                placeholder={"Search Place"}
                index={0}
                isVisible={false}
                value={eventData?.location?.title ?? ""}
                onChange={(formattedAddress, loc, link, title) => {
                  const location = {
                    latitude: loc.lat,
                    longitude: loc.lng,
                    title: capitalizeFirstLetter(title),
                    address: capitalizeFirstLetter(formattedAddress),
                  };
                  updateContentData({
                    location,
                  });
                }}
                disabled={!isEditable}
              />
              {error && <span className="block mt-1 mb-2 text-sm text-red-600 dark:text-red-500">{error}</span>}
            </div>
          )}
        </div>
        <div>
          <AddTextAreaField
            name={"eventdescription"}
            title={"Add Event Description"}
            note={"Instruction, URL links, or explainer of the event."}
            placeholder={"Event Description"}
            index={4}
            height={110}
            charAllowed={300}
            value={eventData?.eventDescription ?? ""}
            onChange={async e => {
              const newValue = e.target.value;
              if (eventData?.eventDescription !== newValue) {
                updateContentData({
                  eventDescription: newValue,
                } as SKEventArtifact);
              }
            }}
            disabled={!isEditable}
          />
        </div>
      </div>
    </>
  );
}
